import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Heart, Check, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";
import { Button } from "@/components/ui/button";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({
    loading: true,
    error: null,
    email: null,
    unsubscribed: false,
    event_notifications: true,
  });
  const [confirming, setConfirming] = useState(null); // 'events' | 'all' | null
  const [done, setDone] = useState(null); // 'events' | 'all' | null

  useEffect(() => {
    if (!token) {
      setState((s) => ({ ...s, loading: false, error: "Missing token." }));
      return;
    }
    api
      .get(`/public/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) =>
        setState({
          loading: false,
          error: null,
          email: r.data.email,
          unsubscribed: r.data.unsubscribed,
          event_notifications: r.data.event_notifications,
        })
      )
      .catch((err) =>
        setState((s) => ({
          ...s,
          loading: false,
          error:
            err?.response?.data?.detail ||
            "We couldn't find that subscription. The link may have expired.",
        }))
      );
  }, [token]);

  const confirm = async (scope) => {
    setConfirming(scope);
    try {
      await api.post("/public/unsubscribe", { token, scope });
      setDone(scope);
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err?.response?.data?.detail || "Something went wrong. Try again.",
      }));
    } finally {
      setConfirming(null);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <p className="text-white/55 text-sm uppercase tracking-[0.25em]">
          Loading...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div
        className="w-full max-w-2xl surface p-10 lg:p-14 space-y-7"
        data-testid="unsubscribe-page"
      >
        {state.error ? (
          <div className="text-center space-y-4">
            <AlertCircle
              size={42}
              strokeWidth={1}
              style={{ color: "var(--gold)" }}
              className="mx-auto"
            />
            <h1 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light">
              Hmm — that link didn't{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                work.
              </em>
            </h1>
            <p className="text-white/65 text-base max-w-md mx-auto">
              {state.error}
            </p>
            <Link
              to="/"
              className="inline-block text-xs uppercase tracking-[0.25em] mt-4"
              style={{ color: "var(--gold)" }}
            >
              ← Back to ninacapone.com
            </Link>
          </div>
        ) : done ? (
          <div className="text-center space-y-4" data-testid="unsubscribe-done">
            <Check
              size={42}
              strokeWidth={1.2}
              style={{ color: "var(--gold)" }}
              className="mx-auto"
            />
            <h1 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light">
              {done === "all" ? (
                <>
                  You're{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    fully unsubscribed.
                  </em>
                </>
              ) : (
                <>
                  No more event{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    announcements.
                  </em>
                </>
              )}
            </h1>
            <p className="text-white/65 text-base max-w-md mx-auto leading-relaxed">
              {done === "all"
                ? `${state.email} has been removed from the Inner Circle. We'll miss you. If this was a mistake, just resubscribe from the footer of any page.`
                : `${state.email} will no longer receive show & event announcements. You'll still get the rest of the Inner Circle (weekly wellness drops, new releases). Want to leave entirely instead?`}
            </p>
            {done === "events" && (
              <button
                onClick={() => confirm("all")}
                disabled={confirming === "all"}
                className="text-xs uppercase tracking-[0.25em] text-white/55 hover:text-[color:var(--gold)] mt-2"
                data-testid="unsubscribe-also-all"
              >
                {confirming === "all"
                  ? "Removing..."
                  : "Unsubscribe from everything"}
              </button>
            )}
            <div className="pt-4">
              <Link
                to="/"
                className="inline-block text-xs uppercase tracking-[0.25em]"
                style={{ color: "var(--gold)" }}
              >
                ← Back to ninacapone.com
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <SectionLabel label="Inner Circle · Subscription Settings" />
              <h1 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                Manage your{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  subscription.
                </em>
              </h1>
              <p className="text-white/65 text-sm">
                Hi <span className="text-white/85">{state.email}</span> — pick
                how you want this to go.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={() => confirm("events")}
                disabled={confirming !== null || !state.event_notifications}
                className="w-full surface p-5 text-left flex items-start gap-4 hover:border-[color:var(--gold)]/30 transition-colors disabled:opacity-50"
                data-testid="unsubscribe-events"
              >
                <Heart
                  size={20}
                  strokeWidth={1.4}
                  style={{ color: "var(--gold)" }}
                  className="mt-0.5 shrink-0"
                />
                <div className="space-y-1">
                  <p className="font-serif text-lg leading-tight">
                    Stop event announcements only
                  </p>
                  <p className="text-xs text-white/55">
                    {state.event_notifications
                      ? "You'll keep receiving weekly wellness essays and new release drops, but no more show invites."
                      : "Already opted out of event announcements."}
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => confirm("all")}
                disabled={confirming !== null}
                className="w-full surface p-5 text-left flex items-start gap-4 hover:border-white/30 transition-colors disabled:opacity-50"
                data-testid="unsubscribe-all"
              >
                <AlertCircle
                  size={20}
                  strokeWidth={1.4}
                  className="mt-0.5 shrink-0 text-white/45"
                />
                <div className="space-y-1">
                  <p className="font-serif text-lg leading-tight">
                    Unsubscribe from everything
                  </p>
                  <p className="text-xs text-white/55">
                    Remove me from the Inner Circle entirely. No more emails of
                    any kind.
                  </p>
                </div>
              </button>
            </div>

            <div className="pt-2 text-center">
              <Link
                to="/"
                className="text-xs uppercase tracking-[0.25em] text-white/45 hover:text-[color:var(--gold)] transition-colors"
              >
                ← Stay subscribed, take me home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
